import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Operadora, RecargasService, TipoOperador, Producto } from 'src/app/services/recargas/recargas.service';

@Component({
  selector: 'app-productos',
  templateUrl: './productos.component.html',
  styleUrls: ['./productos.component.css']
})
export class ProductosComponent implements OnInit {

  // Datos de navegación
  operadoraCode: string = '';
  tipoCode: string = '';
  operadora: any = null;
  tipoOperacion: any = null;

  // Estado del componente
  isLoading = true;
  error: string | null = null;
  productos: Producto[] = [];

  // Estado del modal de edición de secuencia
  isEditingSequence = false;
  editingProduct: Producto | null = null;
  editingSequence: any = null;
  sequenceError: string | null = null;
  isSavingSequence = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private recargasService: RecargasService
  ) {}

  ngOnInit(): void {
    this.loadRouteParams();
    this.loadOperadoraData();
    this.loadProductos();
  }

  /**
   * Carga los parámetros de la ruta
   */
  private loadRouteParams(): void {
    this.route.params.subscribe(params => {
      this.operadoraCode = params['operatorCode'];
      this.tipoCode = params['typeCode'];
    });

    // Obtener datos del estado de navegación si están disponibles
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras.state) {
      this.operadora = navigation.extras.state['operadora'];
      this.tipoOperacion = navigation.extras.state['tipoOperacion'];
    }
  }

  /**
   * Carga la información de la operadora si no está disponible
   */
  private async loadOperadoraData(): Promise<void> {
    try {
      // Cargar operadora si no está disponible
      if (!this.operadora && this.operadoraCode) {
        const operadorasResponse = await this.recargasService.getOperators();
        if (operadorasResponse?.resp) {
          this.operadora = operadorasResponse.resp.find((op: any) => op.code === this.operadoraCode);
        }
      }

      // Cargar tipo de operación si no está disponible
      if (!this.tipoOperacion && this.operadoraCode && this.tipoCode) {
        const tiposResponse = await this.recargasService.getTypeOperators(this.operadoraCode);
        if (tiposResponse?.resp) {
          this.tipoOperacion = tiposResponse.resp.find((tipo: any) => tipo.code === this.tipoCode);
        }
      }
    } catch (error) {
      console.error('Error cargando datos de operadora:', error);
    }
  }

  /**
   * Carga los productos desde el servicio
   */
  async loadProductos(): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await this.recargasService.getProducts(this.operadoraCode, this.tipoCode);
      if (response?.resp) {
        // Filtrar solo productos activos y visibles en consola
        this.productos = response.resp.filter((p: Producto) => p.status && p.showConsole);
      } else {
        this.productos = [];
      }
      this.isLoading = false;
    } catch (error: any) {
      console.error('Error cargando productos:', error);
      this.error = 'Error al cargar los productos. Por favor, intente nuevamente.';
      this.isLoading = false;
    }
  }

  /**
   * Vuelve a la lista de tipos de operación
   */
  onVolver(): void {
    this.router.navigate(['/console/recargas/tipos', this.operadoraCode], {
      state: { operadora: this.operadora }
    });
  }

  /**
   * Reintenta cargar los productos
   */
  onRetry(): void {
    this.loadProductos();
  }

  /**
   * TrackBy para optimización del ngFor de productos
   */
  trackByCode(index: number, producto: Producto): string {
    return producto.code;
  }

  /**
   * TrackBy para optimización del ngFor de montos
   */
  trackByMonto(index: number, monto: number): number {
    return monto;
  }

  /**
   * Abre el modal para editar la secuencia de un producto
   */
  onEditSequence(producto: Producto): void {
    this.editingProduct = producto;
    this.isEditingSequence = true;
    this.sequenceError = null;
    
    // Clonar la secuencia actual para edición
    if (producto.params?.sequence?.BS) {
      this.editingSequence = {
        BS: {
          default: producto.params.sequence.BS.default,
          list: [...producto.params.sequence.BS.list]
        }
      };
    } else {
      // Secuencia por defecto si no existe
      this.editingSequence = {
        BS: {
          default: producto.min || 50,
          list: [50, 100, 200, 500]
        }
      };
    }
  }

  /**
   * Cierra el modal de edición de secuencia
   */
  onCloseSequenceModal(): void {
    this.isEditingSequence = false;
    this.editingProduct = null;
    this.editingSequence = null;
    this.sequenceError = null;
    this.isSavingSequence = false;
  }

  /**
   * Agrega un nuevo monto a la lista
   */
  onAddNewAmount(value: string): void {
    if (!this.editingSequence?.BS?.list || !value) return;
    
    const newAmount = parseInt(value);
    if (isNaN(newAmount) || newAmount <= 0) {
      this.sequenceError = 'Ingrese un monto válido mayor a 0';
      return;
    }

    if (this.editingSequence.BS.list.includes(newAmount)) {
      this.sequenceError = 'Este monto ya existe en la lista';
      return;
    }

    // Limpiar error previo
    this.sequenceError = null;
    
    // Agregar y ordenar
    this.editingSequence.BS.list.push(newAmount);
    this.editingSequence.BS.list.sort((a: number, b: number) => a - b);

    // Establecer el primer monto como por defecto si no hay uno establecido
    if (!this.editingSequence.BS.default || this.editingSequence.BS.list.length === 1) {
      this.editingSequence.BS.default = this.editingSequence.BS.list[0];
    }
  }

  /**
   * Elimina un monto de la lista
   */
  onRemoveAmount(amount: number): void {
    if (!this.editingSequence?.BS?.list) return;
    
    const index = this.editingSequence.BS.list.indexOf(amount);
    if (index > -1) {
      this.editingSequence.BS.list.splice(index, 1);
      
      // Si eliminamos el monto por defecto, establecer el primero como nuevo por defecto
      if (amount === this.editingSequence.BS.default && this.editingSequence.BS.list.length > 0) {
        this.editingSequence.BS.default = this.editingSequence.BS.list[0];
      }
    }
  }

  /**
   * Establece el monto por defecto
   */
  onSetDefault(amount: number): void {
    if (this.editingSequence?.BS) {
      this.editingSequence.BS.default = amount;
    }
  }

  /**
   * Actualiza un monto en la lista
   */
  onUpdateAmount(oldAmount: number, newAmount: number): void {
    if (!this.editingSequence?.BS?.list || newAmount <= 0) return;
    
    const index = this.editingSequence.BS.list.indexOf(oldAmount);
    if (index > -1) {
      this.editingSequence.BS.list[index] = newAmount;
      this.editingSequence.BS.list.sort((a: number, b: number) => a - b);
    }
  }

  /**
   * Guarda los cambios de la secuencia
   */
  async onSaveSequence(): Promise<void> {
    if (!this.editingProduct || !this.editingSequence) return;

    this.isSavingSequence = true;
    this.sequenceError = null;

    try {
      // Validar que el monto por defecto esté en la lista
      if (!this.editingSequence.BS.list.includes(this.editingSequence.BS.default)) {
        this.sequenceError = 'El monto por defecto debe estar incluido en la lista de montos disponibles';
        this.isSavingSequence = false;
        return;
      }

      // Validar que haya al menos un monto
      if (this.editingSequence.BS.list.length === 0) {
        this.sequenceError = 'Debe incluir al menos un monto en la lista';
        this.isSavingSequence = false;
        return;
      }

      // Actualizar secuencia en el servidor
      const response = await this.recargasService.updateProductSequence(
        this.editingSequence,
        this.editingProduct.code
      );

      if (response?.res) {
        // Actualizar el producto local
        this.editingProduct.params = {
          ...this.editingProduct.params,
          sequence: { ...this.editingSequence }
        };

        // Cerrar modal
        this.onCloseSequenceModal();
        
        // Mostrar mensaje de éxito (opcional)
        console.log('Secuencia actualizada exitosamente');
      } else {
        this.sequenceError = 'Error al actualizar la secuencia. Intente nuevamente.';
      }
    } catch (error: any) {
      console.error('Error guardando secuencia:', error);
      this.sequenceError = error?.error?.msg || 'Error al actualizar la secuencia. Intente nuevamente.';
    } finally {
      this.isSavingSequence = false;
    }
  }
}
